/**
 * The form behind every staff change and every record opened outside a queue: a required reason,
 * then one action. The button stays disabled until the reason passes the same `staffReason` rule
 * the Worker applies, so the form never sends what the Worker would refuse for its reason.
 */
import * as stylex from "@stylexjs/stylex";
import type { FormEvent, ReactNode } from "react";
import { useState } from "react";
import { STAFF_REASON_MAX, staffReason } from "@lattice/core";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { SaveLine } from "./bits.tsx";
import { publishNotice } from "./notice.ts";
import { Button, ErrorText, Field } from "./ui.tsx";
export interface ReasonFormProps {
  readonly id: string;
  readonly hint: string;
  readonly action: string;
  readonly tone?: "primary" | "danger";
  /** Resolves to a message to show, or null when the action succeeded. */
  readonly onSubmit: (reason: string) => Promise<string | null>;
  readonly onCancel?: () => void;
  /** Fields that come before the reason, such as where a link moves to. */
  readonly children?: ReactNode;
  /** False while those fields are incomplete. */
  readonly ready?: boolean;
  /** For a page whose only task is giving the reason. */
  readonly autoFocus?: boolean;
}
export function ReasonForm({
  id,
  hint,
  action,
  tone = "primary",
  onSubmit,
  onCancel,
  children,
  ready = true,
  autoFocus = false
}: ReasonFormProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const valid = staffReason(reason);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (valid === null || phase === "saving") return;
    setPhase("saving");
    setError(null);
    publishNotice("saving", "Saving");
    const message = await onSubmit(valid);
    if (message === null) {
      setPhase("saved");
      setReason("");
      publishNotice("saved", "Saved");
      return;
    }
    setPhase("failed");
    setError(message);
    publishNotice("failed", message);
  }
  return <form onSubmit={submit} className="x78zum5 xdt5ytf x1r0x45p">
      {children}
      <Field id={id} label="Reason" hint={`Required. ${hint}`} value={reason} onChange={setReason} multiline maxLength={STAFF_REASON_MAX} autoFocus={autoFocus} />
      <SaveLine phase={phase} />
      {error === null ? null : <ErrorText>{error}</ErrorText>}
      <div className="x78zum5 x1a02dak x1173whf">
        <Button type="submit" tone={tone} disabled={phase === "saving" || valid === null || !ready}>
          {phase === "saving" ? "Saving" : action}
        </Button>
        {onCancel === undefined ? null : <Button tone="quiet" onClick={onCancel}>
            Cancel
          </Button>}
      </div>
    </form>;
}